import type { MembershipRole } from './team.js';

/**
 * ملفّاتُ الصلاحيات — ما يُتاح من كلّ وحدةٍ ولمن.
 *
 * الدورُ (مالك، مدير نظام، مدير، مبيعات، موظّف) رتبةٌ تحكم ما تسمح به
 * الشاشاتُ من عمليات. والملفُّ طبقةٌ فوقه تضيّق لا توسّع: يقول لكلّ وحدةٍ
 * «لا شيء، عرض، تعديل، إدارة». فمن دورُه «مدير» وملفُّه يقصر الحساباتِ على
 * «عرض» يرى الفواتيرَ ولا يكتب فيها.
 *
 * وبلا ملفٍّ يبقى الدورُ وحدَه كما كان — فلا ينكسر شيءٌ لمن لم يضبط ملفّات.
 *
 * والمستوى يُترجَم إلى فعلٍ على الخادم: القراءةُ عرضٌ، والإضافةُ والتعديلُ
 * تعديلٌ، والحذفُ إدارة. والواجهةُ تُخفي وحدةً مستواها «لا شيء».
 */

export const PROFILE_LEVELS = ['NONE', 'VIEW', 'EDIT', 'MANAGE'] as const;
export type ProfileLevel = (typeof PROFILE_LEVELS)[number];

export const PROFILE_LEVEL_RANK: Readonly<Record<ProfileLevel, number>> = {
  NONE: 0,
  VIEW: 1,
  EDIT: 2,
  MANAGE: 3,
};

export const PROFILE_LEVEL_DEFS: readonly { key: ProfileLevel; label: string; hint: string }[] = [
  { key: 'NONE', label: 'لا شيء', hint: 'الوحدة مخفيّة ولا تُقرأ' },
  { key: 'VIEW', label: 'عرض', hint: 'قراءةٌ بلا إضافةٍ ولا تعديل' },
  { key: 'EDIT', label: 'تعديل', hint: 'إضافةٌ وتعديلٌ بلا حذف' },
  { key: 'MANAGE', label: 'إدارة', hint: 'كلُّ شيء بما فيه الحذفُ والإعداد' },
];

export const ACCESS_MODULES = [
  'crm',
  'callcenter',
  'sales',
  'agency',
  'finance',
  'hr',
  'monitor',
  'reports',
  'settings',
] as const;
export type AccessModule = (typeof ACCESS_MODULES)[number];

export interface AccessModuleDef {
  key: AccessModule;
  label: string;
  description: string;
  /** بادئاتُ مسارات الخادم التي تخضع لهذه الوحدة. */
  apiPrefixes: readonly string[];
}

export const ACCESS_MODULE_DEFS: readonly AccessModuleDef[] = [
  {
    key: 'crm',
    label: 'إدارة العملاء',
    description: 'العملاء المحتملون والعملاء وجهات الاتصال والأنشطة والقنوات والتقويم',
    apiPrefixes: ['/crm', '/calendar', '/meta', '/ratings', '/mail', '/customfields'],
  },
  {
    key: 'callcenter',
    label: 'مركز الاتصال',
    description: 'الكونسول والمكالمات وسجلّها وجودتها',
    apiPrefixes: ['/callcenter', '/commissions'],
  },
  {
    key: 'sales',
    label: 'المبيعات',
    description: 'الصفقات والقمع وعروض الأسعار والعقود والكتالوج والعمولات',
    apiPrefixes: ['/sales', '/booking'],
  },
  {
    key: 'agency',
    label: 'إدارة المشاريع',
    description: 'المشاريع والمهامّ والمحافظ وباقات العملاء',
    apiPrefixes: ['/agency'],
  },
  {
    key: 'finance',
    label: 'الحسابات',
    description: 'الفواتير والتحصيل والمصروفات والدفتر العام والأصول',
    apiPrefixes: ['/finance', '/accounting', '/assets', '/payables', '/credit', '/recurring'],
  },
  {
    key: 'hr',
    label: 'الموارد البشرية',
    description: 'ملفّات الموظفين والحضور والإجازات والرواتب والتوظيف',
    apiPrefixes: ['/hr'],
  },
  {
    key: 'monitor',
    label: 'مركز المراقبة',
    description: 'شاشة المراقبة واللقطات والمشاهدة الحيّة وإعداداتها',
    apiPrefixes: ['/monitor', '/callcenter/monitor', '/callcenter/monitoring', '/callcenter/settings/monitoring'],
  },
  {
    key: 'reports',
    label: 'التقارير',
    description: 'لوحات الأداء وباني التقارير وجوي',
    apiPrefixes: ['/reports', '/joy'],
  },
  {
    key: 'settings',
    label: 'الإعدادات',
    description: 'إعداداتُ الشركة والوحدات والمستخدمين والتكاملات (لا الملفَّ الشخصيّ)',
    apiPrefixes: ['/settings', '/team', '/audit', '/data', '/integrations', '/custom-fields'],
  },
];

export const ACCESS_MODULE_MAP = Object.fromEntries(
  ACCESS_MODULE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AccessModule, AccessModuleDef>>;

/**
 * صلاحياتُ ملفّ: وحدةٌ → مستوى. ما لم يُذكر يُعدّ «إدارة» (لا تضييق).
 *
 * و`__actions` — إن وُجدت — صلاحياتٌ تفصيليّةٌ لكلّ فعلٍ على حدة (نمط زوهو):
 * قائمةُ مفاتيحِ الأفعالِ المسموحة. حين تُضبط لوحدةٍ، تُفحص الأفعالُ
 * المُعلَّمةُ بها بدل مستوى الوحدة؛ وما لم يُعلَّم يبقى على المستوى. وملفٌّ
 * بلا `__actions` يبقى كما كان — مستوياتِ وحداتٍ فقط، بلا كسر.
 */
export interface AccessPermissions extends Partial<Record<AccessModule, ProfileLevel>> {
  __actions?: string[];
}

/* ==========================================================================
   الصلاحياتُ التفصيليّة — كلُّ فعلٍ صغيرٍ وكبيرٍ على حدة (نمط زوهو)
   ========================================================================== */

/** فعلٌ مفردٌ يُمنح أو يُمنع، وما يلزمه من أفعالٍ تُمنح معه. */
export interface PermActionDef {
  key: string;
  label: string;
  /** أفعالٌ لا يقوم هذا الفعلُ إلا بها — تُحدَّد معه تلقائيًّا. */
  requires?: readonly string[];
}

/** مجموعةُ أفعالٍ تخصّ جزءًا من وحدة (كالعملاء المحتملين في إدارة العملاء). */
export interface PermGroupDef {
  key: string;
  label: string;
  module: AccessModule;
  actions: readonly PermActionDef[];
}

/**
 * كتالوجُ الأفعالِ التفصيليّة.
 *
 * يبدأ بوحدةِ «إدارة العملاء» كاملةً نموذجًا، وتُضاف الوحداتُ الأخرى على
 * منواله. الأفعالُ مترابطةٌ باعتماديّاتها: «التحويل إلى عميل» يلزمه «إضافة
 * عميل»، و«التصدير» و«تغيير المرحلة» و«تغيير المسؤول» يلزمها «العرض».
 */
export const PERMISSION_GROUPS: readonly PermGroupDef[] = [
  {
    key: 'crm.leads',
    label: 'العملاء المحتملون',
    module: 'crm',
    actions: [
      { key: 'crm.leads.view', label: 'عرض العملاء المحتملين' },
      { key: 'crm.leads.create', label: 'إضافة عميل محتمل', requires: ['crm.leads.view'] },
      { key: 'crm.leads.edit', label: 'تعديل بيانات العملاء المحتملين', requires: ['crm.leads.view'] },
      {
        key: 'crm.leads.changeStage',
        label: 'تغيير المرحلة/الحالة',
        requires: ['crm.leads.view'],
      },
      {
        key: 'crm.leads.reassign',
        label: 'تغيير المسؤول (السيلز)',
        requires: ['crm.leads.view'],
      },
      {
        key: 'crm.leads.convert',
        label: 'تحويل إلى عميل',
        requires: ['crm.leads.view', 'crm.accounts.create'],
      },
      { key: 'crm.leads.delete', label: 'حذف عميل محتمل', requires: ['crm.leads.view'] },
      { key: 'crm.leads.export', label: 'تصدير البيانات', requires: ['crm.leads.view'] },
      {
        key: 'crm.leads.import',
        label: 'استيراد العملاء المحتملين',
        requires: ['crm.leads.view', 'crm.leads.create'],
      },
    ],
  },
  {
    key: 'crm.accounts',
    label: 'العملاء (الحسابات)',
    module: 'crm',
    actions: [
      { key: 'crm.accounts.view', label: 'عرض العملاء' },
      { key: 'crm.accounts.create', label: 'إضافة عميل', requires: ['crm.accounts.view'] },
      { key: 'crm.accounts.edit', label: 'تعديل بيانات العملاء', requires: ['crm.accounts.view'] },
      {
        key: 'crm.accounts.reassign',
        label: 'تغيير المسؤول (السيلز)',
        requires: ['crm.accounts.view'],
      },
      { key: 'crm.accounts.delete', label: 'حذف عميل', requires: ['crm.accounts.view'] },
      { key: 'crm.accounts.export', label: 'تصدير البيانات', requires: ['crm.accounts.view'] },
    ],
  },
  {
    key: 'crm.contacts',
    label: 'جهات الاتصال',
    module: 'crm',
    actions: [
      { key: 'crm.contacts.view', label: 'عرض جهات الاتصال' },
      { key: 'crm.contacts.create', label: 'إضافة جهة اتصال', requires: ['crm.contacts.view'] },
      { key: 'crm.contacts.edit', label: 'تعديل جهات الاتصال', requires: ['crm.contacts.view'] },
      { key: 'crm.contacts.delete', label: 'حذف جهة اتصال', requires: ['crm.contacts.view'] },
      { key: 'crm.contacts.export', label: 'تصدير البيانات', requires: ['crm.contacts.view'] },
    ],
  },
  {
    key: 'crm.activities',
    label: 'الأنشطة (مكالمات/اجتماعات/مهام)',
    module: 'crm',
    actions: [
      { key: 'crm.activities.view', label: 'عرض الأنشطة' },
      { key: 'crm.activities.create', label: 'إضافة نشاط', requires: ['crm.activities.view'] },
      { key: 'crm.activities.edit', label: 'تعديل نشاط', requires: ['crm.activities.view'] },
      {
        key: 'crm.activities.complete',
        label: 'إغلاق/إتمام/إعادة فتح نشاط',
        requires: ['crm.activities.view'],
      },
      { key: 'crm.activities.delete', label: 'حذف نشاط', requires: ['crm.activities.view'] },
    ],
  },
  {
    key: 'crm.channels',
    label: 'القنوات والتقويم',
    module: 'crm',
    actions: [
      { key: 'crm.channels.view', label: 'عرض القنوات والتقويم' },
      { key: 'crm.channels.send', label: 'إرسال رسائل من القنوات', requires: ['crm.channels.view'] },
      {
        key: 'crm.channels.settings',
        label: 'تعديل إعدادات القنوات',
        requires: ['crm.channels.view'],
      },
    ],
  },

  /* ------------------------------ المبيعات ------------------------------ */
  {
    key: 'sales.deals',
    label: 'الصفقات',
    module: 'sales',
    actions: [
      { key: 'sales.deals.view', label: 'عرض الصفقات والقمع والتحليلات' },
      { key: 'sales.deals.create', label: 'إضافة صفقة', requires: ['sales.deals.view'] },
      { key: 'sales.deals.edit', label: 'تعديل بيانات الصفقة (القيمة/التفاصيل)', requires: ['sales.deals.view'] },
      { key: 'sales.deals.changeStage', label: 'تغيير مرحلة الصفقة', requires: ['sales.deals.view'] },
      { key: 'sales.deals.reassign', label: 'تغيير مالك الصفقة', requires: ['sales.deals.view'] },
      { key: 'sales.deals.close', label: 'إغلاق الصفقة (ربح/خسارة)', requires: ['sales.deals.view'] },
      { key: 'sales.deals.reopen', label: 'إعادة فتح صفقة مغلقة', requires: ['sales.deals.view'] },
      { key: 'sales.deals.delete', label: 'حذف صفقة', requires: ['sales.deals.view'] },
    ],
  },
  {
    key: 'sales.quotations',
    label: 'عروض الأسعار',
    module: 'sales',
    actions: [
      { key: 'sales.quotations.view', label: 'عرض عروض الأسعار' },
      { key: 'sales.quotations.create', label: 'إنشاء عرض سعر', requires: ['sales.quotations.view'] },
      { key: 'sales.quotations.edit', label: 'تعديل عرض السعر', requires: ['sales.quotations.view'] },
      { key: 'sales.quotations.send', label: 'إرسال العرض ومتابعته', requires: ['sales.quotations.view'] },
      { key: 'sales.quotations.decide', label: 'تسجيل قبول/رفض العرض', requires: ['sales.quotations.view'] },
      { key: 'sales.quotations.delete', label: 'حذف عرض سعر', requires: ['sales.quotations.view'] },
    ],
  },
  {
    key: 'sales.contracts',
    label: 'العقود',
    module: 'sales',
    actions: [
      { key: 'sales.contracts.view', label: 'عرض العقود' },
      { key: 'sales.contracts.create', label: 'إنشاء عقد', requires: ['sales.contracts.view'] },
      { key: 'sales.contracts.edit', label: 'تعديل العقد', requires: ['sales.contracts.view'] },
      { key: 'sales.contracts.sign', label: 'توقيع العقد', requires: ['sales.contracts.view'] },
      { key: 'sales.contracts.cancel', label: 'إلغاء العقد', requires: ['sales.contracts.view'] },
      { key: 'sales.contracts.delete', label: 'حذف عقد', requires: ['sales.contracts.view'] },
    ],
  },
  {
    key: 'sales.catalog',
    label: 'الكتالوج (الخدمات والباقات)',
    module: 'sales',
    actions: [
      { key: 'sales.catalog.view', label: 'عرض الخدمات والباقات' },
      { key: 'sales.catalog.create', label: 'إضافة خدمة/باقة', requires: ['sales.catalog.view'] },
      { key: 'sales.catalog.edit', label: 'تعديل خدمة/باقة (والأسعار)', requires: ['sales.catalog.view'] },
      { key: 'sales.catalog.delete', label: 'حذف خدمة/باقة', requires: ['sales.catalog.view'] },
    ],
  },

  /* ------------------------------ الحسابات ------------------------------ */
  {
    key: 'finance.invoices',
    label: 'الفواتير',
    module: 'finance',
    actions: [
      { key: 'finance.invoices.view', label: 'عرض الفواتير' },
      { key: 'finance.invoices.create', label: 'إنشاء فاتورة', requires: ['finance.invoices.view'] },
      { key: 'finance.invoices.edit', label: 'تعديل فاتورة', requires: ['finance.invoices.view'] },
      { key: 'finance.invoices.send', label: 'إرسال فاتورة', requires: ['finance.invoices.view'] },
      { key: 'finance.invoices.cancel', label: 'إلغاء فاتورة', requires: ['finance.invoices.view'] },
    ],
  },
  {
    key: 'finance.payments',
    label: 'التحصيل والمدفوعات',
    module: 'finance',
    actions: [
      { key: 'finance.payments.view', label: 'عرض المدفوعات' },
      { key: 'finance.payments.create', label: 'تسجيل دفعة', requires: ['finance.payments.view'] },
      { key: 'finance.payments.confirm', label: 'تأكيد دفعة', requires: ['finance.payments.view'] },
      { key: 'finance.payments.delete', label: 'حذف دفعة', requires: ['finance.payments.view'] },
    ],
  },
  {
    key: 'finance.expenses',
    label: 'المصروفات',
    module: 'finance',
    actions: [
      { key: 'finance.expenses.view', label: 'عرض المصروفات' },
      { key: 'finance.expenses.create', label: 'تسجيل مصروف', requires: ['finance.expenses.view'] },
      { key: 'finance.expenses.edit', label: 'تعديل مصروف', requires: ['finance.expenses.view'] },
      { key: 'finance.expenses.delete', label: 'حذف مصروف', requires: ['finance.expenses.view'] },
    ],
  },
  {
    key: 'finance.payables',
    label: 'المشتريات والموردون',
    module: 'finance',
    actions: [
      { key: 'finance.payables.view', label: 'عرض الموردين وفواتير الشراء' },
      { key: 'finance.payables.create', label: 'إضافة مورّد/فاتورة شراء', requires: ['finance.payables.view'] },
      { key: 'finance.payables.edit', label: 'تعديل مورّد/فاتورة شراء', requires: ['finance.payables.view'] },
      { key: 'finance.payables.pay', label: 'استلام/سداد فواتير الشراء', requires: ['finance.payables.view'] },
      { key: 'finance.payables.delete', label: 'حذف مورّد/فاتورة شراء', requires: ['finance.payables.view'] },
    ],
  },
  {
    key: 'finance.accounting',
    label: 'الدفتر العام والقيود',
    module: 'finance',
    actions: [
      { key: 'finance.accounting.view', label: 'عرض الحسابات والقيود' },
      {
        key: 'finance.accounting.manage',
        label: 'إدارة الحسابات والقيود (إضافة/تعديل/ترحيل/عكس/حذف)',
        requires: ['finance.accounting.view'],
      },
    ],
  },
  {
    key: 'finance.assets',
    label: 'الأصول',
    module: 'finance',
    actions: [
      { key: 'finance.assets.view', label: 'عرض الأصول' },
      {
        key: 'finance.assets.manage',
        label: 'إدارة الأصول (إضافة/تعديل/عهدة/استبعاد/إهلاك/حذف)',
        requires: ['finance.assets.view'],
      },
    ],
  },

  /* --------------------------- الموارد البشرية --------------------------- */
  {
    key: 'hr.employees',
    label: 'ملفّات الموظفين',
    module: 'hr',
    actions: [
      { key: 'hr.employees.view', label: 'عرض ملفّات الموظفين' },
      { key: 'hr.employees.create', label: 'إضافة موظف', requires: ['hr.employees.view'] },
      { key: 'hr.employees.edit', label: 'تعديل ملفّ موظف', requires: ['hr.employees.view'] },
      { key: 'hr.employees.terminate', label: 'إنهاء خدمة موظف', requires: ['hr.employees.view'] },
    ],
  },
  {
    key: 'hr.attendance',
    label: 'الحضور والإجازات الرسمية',
    module: 'hr',
    actions: [
      { key: 'hr.attendance.view', label: 'عرض الحضور' },
      { key: 'hr.attendance.create', label: 'تسجيل/تعديل الحضور والعطلات', requires: ['hr.attendance.view'] },
      { key: 'hr.attendance.delete', label: 'حذف عطلة', requires: ['hr.attendance.view'] },
    ],
  },
  {
    key: 'hr.leaves',
    label: 'الإجازات والطلبات',
    module: 'hr',
    actions: [
      { key: 'hr.leaves.view', label: 'عرض الإجازات والطلبات' },
      { key: 'hr.leaves.create', label: 'تقديم إجازة/طلب', requires: ['hr.leaves.view'] },
      { key: 'hr.leaves.decide', label: 'اعتماد/رفض الإجازات والطلبات', requires: ['hr.leaves.view'] },
    ],
  },
  {
    key: 'hr.payroll',
    label: 'الرواتب',
    module: 'hr',
    actions: [
      { key: 'hr.payroll.view', label: 'عرض الرواتب' },
      { key: 'hr.payroll.create', label: 'تشغيل مسيّر/إضافة تسويات', requires: ['hr.payroll.view'] },
      { key: 'hr.payroll.approve', label: 'اعتماد مسيّر الرواتب', requires: ['hr.payroll.view'] },
      { key: 'hr.payroll.pay', label: 'تعليم الرواتب مدفوعة', requires: ['hr.payroll.view'] },
      { key: 'hr.payroll.delete', label: 'حذف مسيّر/تسوية', requires: ['hr.payroll.view'] },
    ],
  },

  /* ---------------------------- إدارة المشاريع ---------------------------- */
  {
    key: 'agency.projects',
    label: 'المشاريع',
    module: 'agency',
    actions: [
      { key: 'agency.projects.view', label: 'عرض المشاريع' },
      { key: 'agency.projects.create', label: 'إنشاء مشروع', requires: ['agency.projects.view'] },
      { key: 'agency.projects.edit', label: 'تعديل مشروع (الأقسام والحقول)', requires: ['agency.projects.view'] },
      { key: 'agency.projects.delete', label: 'حذف مشروع', requires: ['agency.projects.view'] },
    ],
  },
  {
    key: 'agency.tasks',
    label: 'المهامّ',
    module: 'agency',
    actions: [
      { key: 'agency.tasks.view', label: 'عرض المهامّ' },
      { key: 'agency.tasks.create', label: 'إضافة مهمّة', requires: ['agency.tasks.view'] },
      { key: 'agency.tasks.edit', label: 'تعديل/نقل/إعادة فتح مهمّة', requires: ['agency.tasks.view'] },
      { key: 'agency.tasks.delete', label: 'حذف مهمّة', requires: ['agency.tasks.view'] },
    ],
  },
  {
    key: 'agency.templates',
    label: 'قوالب المشاريع والمحافظ',
    module: 'agency',
    actions: [
      { key: 'agency.templates.view', label: 'عرض القوالب والمحافظ' },
      { key: 'agency.templates.create', label: 'إنشاء قالب/محفظة', requires: ['agency.templates.view'] },
      { key: 'agency.templates.edit', label: 'تعديل/تطبيق قالب', requires: ['agency.templates.view'] },
      { key: 'agency.templates.delete', label: 'حذف قالب/محفظة', requires: ['agency.templates.view'] },
    ],
  },

  /* ----------------------------- مركز الاتصال ----------------------------- */
  {
    key: 'callcenter.calls',
    label: 'المكالمات',
    module: 'callcenter',
    actions: [
      { key: 'callcenter.calls.view', label: 'عرض المكالمات وسجلّها' },
      { key: 'callcenter.calls.create', label: 'تسجيل/بدء مكالمة', requires: ['callcenter.calls.view'] },
      { key: 'callcenter.calls.edit', label: 'إنهاء/تحويل/ربط المكالمة', requires: ['callcenter.calls.view'] },
      { key: 'callcenter.calls.review', label: 'مراجعة جودة المكالمة', requires: ['callcenter.calls.view'] },
    ],
  },
  {
    key: 'callcenter.dispositions',
    label: 'نتائج المكالمات (Dispositions)',
    module: 'callcenter',
    actions: [
      { key: 'callcenter.dispositions.view', label: 'عرض النتائج' },
      { key: 'callcenter.dispositions.create', label: 'إضافة نتيجة', requires: ['callcenter.dispositions.view'] },
      { key: 'callcenter.dispositions.edit', label: 'تعديل نتيجة', requires: ['callcenter.dispositions.view'] },
    ],
  },

  /* ------------------------------- التقارير ------------------------------ */
  {
    key: 'reports.builder',
    label: 'التقارير',
    module: 'reports',
    actions: [
      { key: 'reports.builder.view', label: 'عرض التقارير وتشغيلها' },
      { key: 'reports.builder.export', label: 'تصدير بيانات التقارير', requires: ['reports.builder.view'] },
      { key: 'reports.builder.manage', label: 'حفظ/تعديل/حذف التقارير المحفوظة', requires: ['reports.builder.view'] },
    ],
  },
  {
    key: 'reports.joy',
    label: 'المساعد الذكيّ (جوي)',
    module: 'reports',
    actions: [
      { key: 'reports.joy.use', label: 'استخدام جوي (المحادثة والتشغيل)' },
      { key: 'reports.joy.manage', label: 'إدارة مهامّ ومراقبات جوي', requires: ['reports.joy.use'] },
    ],
  },

  /* ------------------------------- المراقبة ------------------------------ */
  {
    key: 'monitor.screens',
    label: 'مركز المراقبة',
    module: 'monitor',
    actions: [
      { key: 'monitor.screens.view', label: 'عرض المراقبة واللقطات والمشاهدة الحيّة' },
      { key: 'monitor.screens.manage', label: 'إدارة إعدادات المراقبة واللقطات', requires: ['monitor.screens.view'] },
    ],
  },

  /* ------------------------------- الإعدادات ------------------------------ */
  {
    key: 'settings.users',
    label: 'المستخدمون والأدوار',
    module: 'settings',
    actions: [
      { key: 'settings.users.view', label: 'عرض المستخدمين وملفّات الصلاحيات' },
      { key: 'settings.users.manage', label: 'إضافة/تعديل المستخدمين وملفّات الصلاحيات', requires: ['settings.users.view'] },
    ],
  },
  {
    key: 'settings.company',
    label: 'إعدادات الشركة',
    module: 'settings',
    actions: [
      { key: 'settings.company.view', label: 'عرض إعدادات الشركة والوحدات' },
      { key: 'settings.company.manage', label: 'تعديل إعدادات الشركة والوحدات', requires: ['settings.company.view'] },
    ],
  },
  {
    key: 'settings.integrations',
    label: 'التكاملات',
    module: 'settings',
    actions: [
      { key: 'settings.integrations.view', label: 'عرض التكاملات' },
      { key: 'settings.integrations.manage', label: 'ربط/تعديل التكاملات', requires: ['settings.integrations.view'] },
    ],
  },
  {
    key: 'settings.data',
    label: 'البيانات (استيراد/تصدير/حذف)',
    module: 'settings',
    actions: [
      { key: 'settings.data.view', label: 'عرض أدوات البيانات وسجلّ التدقيق' },
      { key: 'settings.data.manage', label: 'استيراد/تصدير/حذف البيانات', requires: ['settings.data.view'] },
    ],
  },
];

/** كلُّ الأفعالِ مُفهرسةً بمفتاحها. */
export const PERMISSION_ACTION_MAP: Readonly<Record<string, PermActionDef & { module: AccessModule }>> =
  Object.fromEntries(
    PERMISSION_GROUPS.flatMap((g) => g.actions.map((a) => [a.key, { ...a, module: g.module }])),
  );

/** الوحداتُ التي لها كتالوجُ أفعالٍ تفصيليّة (الباقي مستوياتُ وحداتٍ فقط). */
export const MODULES_WITH_ACTIONS: ReadonlySet<AccessModule> = new Set(
  PERMISSION_GROUPS.map((g) => g.module),
);

/**
 * الإغلاقُ الاعتماديّ: فعلٌ يجرّ ما يلزمه، وما يلزمُ ما يلزمه — حتى تستقرّ.
 *
 * يُستعمل في الواجهة: تحديدُ فعلٍ يحدّد معه لوازمَه تلقائيًّا؛ وإزالةُ فعلٍ
 * تُزيل ما يعتمد عليه.
 */
export function withRequired(actionKeys: Iterable<string>): string[] {
  const out = new Set<string>();
  const add = (k: string) => {
    if (out.has(k)) return;
    out.add(k);
    for (const req of PERMISSION_ACTION_MAP[k]?.requires ?? []) add(req);
  };
  for (const k of actionKeys) add(k);
  return [...out];
}

/** الأفعالُ التي تعتمد على فعلٍ ما — تُزال معه إن أُزيل. */
export function dependentsOf(actionKey: string): string[] {
  return Object.values(PERMISSION_ACTION_MAP)
    .filter((a) => a.requires?.includes(actionKey))
    .map((a) => a.key);
}

/**
 * هل يُتاح فعلٌ تفصيليّ؟
 *
 * بلا ملفٍّ: نعم (المالكُ والدورُ وحدَه). وبملفٍّ فيه أفعالٌ تفصيليّة: يُفحص
 * وجودُ الفعلِ فيها. وبملفٍّ بلا أفعالٍ تفصيليّة (قديمٍ أو مستوياتِ وحداتٍ
 * فقط): يُرجَع إلى مستوى الوحدةِ كما كان — فلا ينكسر ملفٌّ قائم.
 */
export function actionAllowed(
  permissions: AccessPermissions | null | undefined,
  actionKey: string,
  fallbackModule: AccessModule,
  fallbackLevel: ProfileLevel,
): boolean {
  if (!permissions) return true;
  if (Array.isArray(permissions.__actions)) return permissions.__actions.includes(actionKey);
  return canAccess(permissions, fallbackModule, fallbackLevel);
}

/** رتبةُ الفعلِ من مستوياتِ الوحدة — بها تُشتقّ مستوياتُ الوحداتِ من الأفعال. */
export function actionBaseLevel(actionKey: string): ProfileLevel {
  if (actionKey.endsWith('.view') || actionKey.endsWith('.export')) return 'VIEW';
  if (actionKey.endsWith('.delete') || actionKey.endsWith('.manage')) return 'MANAGE';
  return 'EDIT';
}

/**
 * اشتقاقُ مستوياتِ الوحداتِ من الأفعالِ المختارة.
 *
 * الواجهةُ صارت أفعالًا تفصيليّةً فقط، لكنّ المساراتِ غيرَ المُخرَّطةِ تُفحص
 * بمستوى الوحدة. فيُشتقّ لكلِّ وحدةٍ أعلى مستوى فعلٍ مُنِح فيها، وما لا فعلَ
 * له يُضبط «لا شيء» صراحةً — فلا يتسرّب مسارٌ غيرُ مُخرَّطٍ إلى «إدارة».
 */
export function deriveModuleLevels(
  actionKeys: Iterable<string>,
): Partial<Record<AccessModule, ProfileLevel>> {
  const out: Partial<Record<AccessModule, ProfileLevel>> = {};
  for (const k of actionKeys) {
    const def = PERMISSION_ACTION_MAP[k];
    if (!def) continue;
    const lvl = actionBaseLevel(k);
    const cur = out[def.module];
    if (!cur || PROFILE_LEVEL_RANK[lvl] > PROFILE_LEVEL_RANK[cur]) out[def.module] = lvl;
  }
  for (const m of ACCESS_MODULES) if (!(m in out)) out[m] = 'NONE';
  return out;
}

/**
 * فرْدُ مستوياتِ وحداتٍ قديمةٍ إلى أفعالٍ تفصيليّة — لتحريرِ ملفٍّ قديمٍ بلا
 * فقدِ صلاحياته: يُمنح من كلِّ وحدةٍ ما رتبتُه ضمن مستواها.
 */
export function expandLevelsToActions(permissions: AccessPermissions): string[] {
  const out: string[] = [];
  for (const g of PERMISSION_GROUPS) {
    const lvl = permissions[g.module] ?? 'MANAGE';
    for (const a of g.actions) {
      if (PROFILE_LEVEL_RANK[actionBaseLevel(a.key)] <= PROFILE_LEVEL_RANK[lvl]) out.push(a.key);
    }
  }
  return withRequired(out);
}

/* ==========================================================================
   خريطةُ المسارات → الأفعال — تحكّمٌ تفصيليٌّ بلا وسمِ كلِّ مسارٍ يدويًّا
   --------------------------------------------------------------------------
   وحداتٌ كثيرةٌ بمساراتٍ قياسيّة (REST) تُربط أفعالُها بمسارِها في جدولٍ
   واحدٍ بدل ديكوريتر لكلّ نقطة. الحارسُ يستشيره حين لا يجد @Perm صريحًا.
   والوسمُ الصريحُ (كإدارة العملاء) يبقى الأدقّ ويسبق هذا الجدول.
   ========================================================================== */

interface RoutePerm {
  method: string;
  re: RegExp;
  action: string;
}

/**
 * أفعالُ جزءٍ قياسيّة على مسارِه.
 *
 * `base` مسارُ الجمع (بلا بادئة /api). تُنتَج: إضافةٌ على POST المسارِ،
 * وتعديلٌ على PATCH العنصر، وحذفٌ على DELETE العنصر، وعرضٌ على أيّ GET
 * تحته. و`extra` أفعالٌ خاصّةٌ على مساراتٍ فرعيّة، تُقدَّم فتُطابَق أوّلًا.
 */
function crud(
  base: string,
  group: string,
  extra: ReadonlyArray<readonly [string, RegExp, string]> = [],
): RoutePerm[] {
  const esc = base.replace(/\//g, '\\/');
  const id = `${esc}\\/[^/]+`;
  return [
    ...extra.map(([method, re, a]) => ({ method, re, action: `${group}.${a}` })),
    { method: 'POST', re: new RegExp(`^${esc}$`), action: `${group}.create` },
    { method: 'PATCH', re: new RegExp(`^${id}$`), action: `${group}.edit` },
    { method: 'PUT', re: new RegExp(`^${id}$`), action: `${group}.edit` },
    { method: 'DELETE', re: new RegExp(`^${id}$`), action: `${group}.delete` },
    { method: 'GET', re: new RegExp(`^${esc}(\\/.*)?$`), action: `${group}.view` },
  ];
}

/** عرضٌ/إدارةٌ لمنطقةٍ مركّبةٍ يصعب فصلُ أفعالها: قراءةٌ على GET، وإدارةٌ على ما سواه. */
function viewManage(base: string, group: string): RoutePerm[] {
  const esc = base.replace(/\//g, '\\/');
  const all = new RegExp(`^${esc}(\\/.*)?$`);
  return [
    { method: 'GET', re: all, action: `${group}.view` },
    { method: 'POST', re: all, action: `${group}.manage` },
    { method: 'PATCH', re: all, action: `${group}.manage` },
    { method: 'PUT', re: all, action: `${group}.manage` },
    { method: 'DELETE', re: all, action: `${group}.manage` },
  ];
}

export const ROUTE_PERMISSIONS: readonly RoutePerm[] = [
  // ------------------------------ المبيعات ------------------------------
  ...crud('/sales/deals', 'sales.deals', [
    ['POST', /^\/sales\/deals\/[^/]+\/close$/, 'close'],
    ['POST', /^\/sales\/deals\/[^/]+\/reopen$/, 'reopen'],
    /* التعديلُ بوّابتُه العرض، وتُفصَّل حقولُه (مرحلة/مالك) في الخدمة. */
    ['PATCH', /^\/sales\/deals\/[^/]+$/, 'view'],
  ]),
  ...crud('/sales/quotations', 'sales.quotations', [
    ['POST', /^\/sales\/quotations\/[^/]+\/send$/, 'send'],
    ['POST', /^\/sales\/quotations\/[^/]+\/follow$/, 'send'],
    ['POST', /^\/sales\/quotations\/[^/]+\/decide$/, 'decide'],
  ]),
  ...crud('/sales/contracts', 'sales.contracts', [
    ['POST', /^\/sales\/contracts\/from-quotation$/, 'create'],
    ['POST', /^\/sales\/contracts\/[^/]+\/sign$/, 'sign'],
    ['POST', /^\/sales\/contracts\/[^/]+\/cancel$/, 'cancel'],
  ]),
  ...crud('/sales/services', 'sales.catalog'),
  ...crud('/sales/packages', 'sales.catalog', [
    ['POST', /^\/sales\/packages\/[^/]+\/expand$/, 'view'],
  ]),

  // ------------------------------ الحسابات ------------------------------
  ...crud('/finance/invoices', 'finance.invoices', [
    ['POST', /^\/finance\/invoices\/from-quotation\/[^/]+$/, 'create'],
    ['POST', /^\/finance\/invoices\/[^/]+\/send$/, 'send'],
    ['POST', /^\/finance\/invoices\/[^/]+\/cancel$/, 'cancel'],
  ]),
  ...crud('/finance/payments', 'finance.payments', [
    ['POST', /^\/finance\/payments\/[^/]+\/confirm$/, 'confirm'],
  ]),
  ...crud('/finance/expenses', 'finance.expenses'),
  ...crud('/finance/suppliers', 'finance.payables'),
  ...crud('/finance/bills', 'finance.payables', [
    ['POST', /^\/finance\/bills\/[^/]+\/receive$/, 'pay'],
    ['POST', /^\/finance\/bills\/[^/]+\/cancel$/, 'edit'],
    ['POST', /^\/finance\/bills\/[^/]+\/payments$/, 'pay'],
  ]),
  { method: 'DELETE', re: /^\/finance\/bill-payments\/[^/]+$/, action: 'finance.payables.pay' },
  ...viewManage('/accounting', 'finance.accounting'),
  ...viewManage('/assets', 'finance.assets'),

  // --------------------------- الموارد البشرية --------------------------
  ...crud('/hr/employees', 'hr.employees', [
    ['POST', /^\/hr\/employees\/[^/]+\/terminate$/, 'terminate'],
  ]),
  ...crud('/hr/attendance', 'hr.attendance'),
  ...crud('/hr/holidays', 'hr.attendance'),
  { method: 'GET', re: /^\/hr\/leaves(\/.*)?$/, action: 'hr.leaves.view' },
  { method: 'POST', re: /^\/hr\/leaves$/, action: 'hr.leaves.create' },
  { method: 'POST', re: /^\/hr\/leaves\/[^/]+\/cancel$/, action: 'hr.leaves.create' },
  { method: 'PATCH', re: /^\/hr\/leaves\/[^/]+\/decide$/, action: 'hr.leaves.decide' },
  { method: 'GET', re: /^\/hr\/requests(\/.*)?$/, action: 'hr.leaves.view' },
  { method: 'POST', re: /^\/hr\/requests\/[^/]+\/decide$/, action: 'hr.leaves.decide' },
  { method: 'POST', re: /^\/hr\/requests\/[^/]+\/reimburse$/, action: 'hr.leaves.decide' },
  ...crud('/hr/payroll/adjustments', 'hr.payroll'),
  ...crud('/hr/payroll/runs', 'hr.payroll', [
    ['POST', /^\/hr\/payroll\/runs\/[^/]+\/approve$/, 'approve'],
    ['POST', /^\/hr\/payroll\/runs\/[^/]+\/paid$/, 'pay'],
  ]),

  // ---------------------------- إدارة المشاريع ---------------------------
  ...crud('/agency/projects', 'agency.projects', [
    ['POST', /^\/agency\/projects\/[^/]+\/sections$/, 'edit'],
    ['POST', /^\/agency\/projects\/[^/]+\/fields$/, 'edit'],
  ]),
  ...crud('/agency/sections', 'agency.projects'),
  ...crud('/agency/fields', 'agency.projects'),
  ...crud('/agency/tasks', 'agency.tasks', [
    ['POST', /^\/agency\/tasks\/[^/]+\/move$/, 'edit'],
    ['POST', /^\/agency\/tasks\/[^/]+\/reopen$/, 'edit'],
  ]),
  ...crud('/agency/templates', 'agency.templates', [
    ['POST', /^\/agency\/templates\/starters$/, 'create'],
    ['POST', /^\/agency\/templates\/[^/]+\/apply$/, 'edit'],
    ['POST', /^\/agency\/templates\/[^/]+\/flags$/, 'edit'],
  ]),
  ...crud('/agency/portfolios', 'agency.templates'),

  // ----------------------------- مركز الاتصال ----------------------------
  ...crud('/callcenter/calls', 'callcenter.calls', [
    ['PATCH', /^\/callcenter\/calls\/[^/]+\/end$/, 'edit'],
    ['PATCH', /^\/callcenter\/calls\/[^/]+\/recording$/, 'edit'],
    ['PATCH', /^\/callcenter\/calls\/[^/]+\/transfer$/, 'edit'],
    ['POST', /^\/callcenter\/calls\/[^/]+\/lead$/, 'edit'],
    ['POST', /^\/callcenter\/calls\/[^/]+\/review$/, 'review'],
    ['POST', /^\/callcenter\/calls\/[^/]+\/rating-link$/, 'edit'],
  ]),
  ...crud('/callcenter/dispositions', 'callcenter.dispositions'),

  // ------------------------------- التقارير ------------------------------
  { method: 'POST', re: /^\/reports\/export$/, action: 'reports.builder.export' },
  { method: 'POST', re: /^\/reports\/saved$/, action: 'reports.builder.manage' },
  { method: 'PATCH', re: /^\/reports\/saved\/[^/]+$/, action: 'reports.builder.manage' },
  { method: 'DELETE', re: /^\/reports\/saved\/[^/]+$/, action: 'reports.builder.manage' },
  { method: 'POST', re: /^\/reports\/run$/, action: 'reports.builder.view' },
  { method: 'GET', re: /^\/reports(\/.*)?$/, action: 'reports.builder.view' },
  { method: 'PATCH', re: /^\/joy\/(tasks|watches)\/[^/]+$/, action: 'reports.joy.manage' },
  { method: 'DELETE', re: /^\/joy\/(tasks|watches)\/[^/]+$/, action: 'reports.joy.manage' },
  { method: 'GET', re: /^\/joy(\/.*)?$/, action: 'reports.joy.use' },
  { method: 'POST', re: /^\/joy(\/.*)?$/, action: 'reports.joy.use' },

  // ------------------------------- المراقبة ------------------------------
  { method: 'POST', re: /^\/callcenter\/monitor\/(watch|unwatch)$/, action: 'monitor.screens.view' },
  { method: 'DELETE', re: /^\/callcenter\/monitor\/captures$/, action: 'monitor.screens.manage' },
  { method: 'PATCH', re: /^\/callcenter\/settings\/monitoring$/, action: 'monitor.screens.manage' },
  { method: 'GET', re: /^\/callcenter\/monitor(\/.*)?$/, action: 'monitor.screens.view' },
  { method: 'GET', re: /^\/callcenter\/monitoring(\/.*)?$/, action: 'monitor.screens.view' },
  ...viewManage('/monitor', 'monitor.screens'),

  // ------------------------------- الإعدادات ------------------------------
  { method: 'GET', re: /^\/team(\/.*)?$/, action: 'settings.users.view' },
  { method: 'POST', re: /^\/team(\/.*)?$/, action: 'settings.users.manage' },
  { method: 'PATCH', re: /^\/team(\/.*)?$/, action: 'settings.users.manage' },
  { method: 'DELETE', re: /^\/team(\/.*)?$/, action: 'settings.users.manage' },
  ...viewManage('/settings', 'settings.company'),
  ...viewManage('/integrations', 'settings.integrations'),
  { method: 'GET', re: /^\/audit(\/.*)?$/, action: 'settings.data.view' },
  ...viewManage('/data', 'settings.data'),
  ...viewManage('/deletion', 'settings.data'),
];

/** الفعلُ الذي يخضع له مسارٌ (method + path) — أو null إن لم يُطابق شيئًا. */
export function actionForApiPath(method: string, path: string): string | null {
  const clean = path.split('?')[0]!;
  const m = method.toUpperCase();
  for (const r of ROUTE_PERMISSIONS) if (r.method === m && r.re.test(clean)) return r.action;
  return null;
}

/**
 * مساراتٌ يحتاجها كلُّ موظّفٍ مهما ضاق ملفُّه — نبضةُ النشاط، وحضورُه،
 * وبياناتُه هو، والقراءةُ العامّة للإعدادات التي تحتاجها المستندات.
 * تُفحص قبل الوحدات، فالأخصُّ يسبق الأعمّ.
 */
const ALWAYS_ALLOWED: readonly string[] = [
  '/callcenter/monitor/heartbeat',
  '/callcenter/monitor/watched',
  '/callcenter/monitor/frame',
  '/callcenter/monitoring/policy',
  '/callcenter/monitoring/shot',
  '/callcenter/monitoring/segment',
  '/callcenter/presence',
  '/callcenter/agent-line',
  '/hr/me',
  '/settings/lookups',
  '/team/members',
  '/team/seats',
];

function startsWithSegment(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`);
}

/**
 * الوحدةُ التي يخضع لها مسارٌ على الخادم — أو null إن كان حرًّا.
 *
 * `path` بلا بادئة `/api`. الأطولُ يسبق الأقصر: `/callcenter/monitor` مراقبةٌ
 * لا مركزَ اتصال، و`/hr/me` حرٌّ لا مواردَ بشرية.
 */
export function moduleForApiPath(path: string): AccessModule | null {
  const clean = path.split('?')[0]!;
  if (ALWAYS_ALLOWED.some((p) => startsWithSegment(clean, p))) return null;

  let best: { key: AccessModule; len: number } | null = null;
  for (const def of ACCESS_MODULE_DEFS) {
    for (const prefix of def.apiPrefixes) {
      if (startsWithSegment(clean, prefix) && (!best || prefix.length > best.len)) {
        best = { key: def.key, len: prefix.length };
      }
    }
  }
  return best?.key ?? null;
}

/** المستوى الذي يتطلّبه فعلُ HTTP. */
export function levelForMethod(method: string): ProfileLevel {
  switch (method.toUpperCase()) {
    case 'GET':
    case 'HEAD':
    case 'OPTIONS':
      return 'VIEW';
    case 'DELETE':
      return 'MANAGE';
    default:
      return 'EDIT';
  }
}

/** هل يسمح الملفُّ بهذا المستوى على الوحدة؟ — بلا ملفٍّ يسمح بكلّ شيء. */
export function canAccess(
  permissions: AccessPermissions | null | undefined,
  module: AccessModule,
  level: ProfileLevel,
): boolean {
  if (!permissions) return true;
  const granted = permissions[module] ?? 'MANAGE';
  return PROFILE_LEVEL_RANK[granted] >= PROFILE_LEVEL_RANK[level];
}

/** ملفٌّ يُقرأ كما يُخزَّن. */
export interface AccessProfileView {
  id: string;
  name: string;
  description: string | null;
  baseRole: MembershipRole;
  permissions: AccessPermissions;
  members: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * ملفّاتٌ جاهزةٌ تُقترح عند أوّل فتح — تُنشأ بضغطة لا تُفرض.
 */
export const ACCESS_PROFILE_PRESETS: readonly {
  name: string;
  description: string;
  baseRole: MembershipRole;
  permissions: AccessPermissions;
}[] = [
  {
    name: 'مندوب مبيعات',
    description: 'عملاؤه وصفقاته وعروضه، ويرى الفواتير ولا يكتبها',
    baseRole: 'EMPLOYEE',
    permissions: {
      crm: 'EDIT',
      callcenter: 'EDIT',
      sales: 'EDIT',
      agency: 'VIEW',
      finance: 'VIEW',
      hr: 'NONE',
      monitor: 'NONE',
      reports: 'VIEW',
      settings: 'NONE',
    },
  },
  {
    name: 'محاسب',
    description: 'الحسابات كاملةً، والعملاء والمبيعات للقراءة',
    baseRole: 'ADMIN',
    permissions: {
      crm: 'VIEW',
      callcenter: 'NONE',
      sales: 'VIEW',
      agency: 'NONE',
      finance: 'MANAGE',
      hr: 'NONE',
      monitor: 'NONE',
      reports: 'VIEW',
      settings: 'NONE',
    },
  },
  {
    name: 'موظف مركز اتصال',
    description: 'الكونسول والعملاء، ولا يرى المال ولا الموارد البشرية',
    baseRole: 'EMPLOYEE',
    permissions: {
      crm: 'EDIT',
      callcenter: 'EDIT',
      sales: 'VIEW',
      agency: 'NONE',
      finance: 'NONE',
      hr: 'NONE',
      monitor: 'NONE',
      reports: 'NONE',
      settings: 'NONE',
    },
  },
  {
    name: 'مدير موارد بشرية',
    description: 'الموارد البشرية كاملةً، والباقي للقراءة',
    baseRole: 'ADMIN',
    permissions: {
      crm: 'VIEW',
      callcenter: 'NONE',
      sales: 'NONE',
      agency: 'VIEW',
      finance: 'NONE',
      hr: 'MANAGE',
      monitor: 'VIEW',
      reports: 'VIEW',
      settings: 'NONE',
    },
  },
];
